import { useEffect, useState } from 'react';
import {
  Briefcase, CheckCircle2, Copy, ExternalLink, Loader2, Mail, PauseCircle,
  PlayCircle, Send, Ban, Clock, Sparkles, ChevronLeft, ChevronRight, Check, Plus, AlertCircle,
} from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';
import type { Job, JobPublishStatus } from '@core/types';
import { jobService } from '@core/supabase/services/jobService';
import { scheduledJobService } from '@core/supabase/services/scheduledJobService';
import { supabase } from '@core/supabase/client';
import {
  buildClientListingApprovalMailto,
  JOB_PUBLISH_LIFECYCLE_HELPER,
  isJobPublishTransitionAllowed,
} from '@core/constants/jobLifecycle';
import {
  JOB_PUBLISH_CHANNEL_DEFS,
  normalizeJobPublishChannels,
  type JobPublishChannelId,
} from '@core/constants/jobPublishChannels';
import { ContextAiAssistModal, AiAssistIconButton } from '@shared/components/ai/ContextAiAssistModal';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

/* ─── helpers ─── */
function publishChannelsDraftMatchesJob(draft: JobPublishChannelId[], jobChannels: unknown): boolean {
  const a = [...draft].sort().join('\0');
  const b = [...normalizeJobPublishChannels(jobChannels)].sort().join('\0');
  return a === b;
}

const APPROVAL_SUBJECT = (title: string) => `Listing approval request — ${title}`;
const APPROVAL_BODY = (title: string, orgName?: string | null) =>
  `Hi,\n\nWe'd like to list the following role on our public careers site and share the posting with candidates.\n\nRole: ${title}\n${orgName ? `Organisation: ${orgName}\n` : ''}\nPlease reply confirming your approval or any requested changes before we proceed.\n\nThank you.`;
const FOLLOWUP_BODY = (title: string) =>
  `Hi,\n\nThis is a follow-up on our earlier request for approval to advertise the \"${title}\" role on our careers site.\n\nCould you please confirm at your earliest convenience?\n\nThank you.`;

/* ─── Publish status pill ─── */
const PS_MAP: Record<string, { bg: string; text: string; label: string }> = {
  draft:          { bg: 'rgba(139,128,249,0.12)', text: '#8B80F9',  label: 'Draft' },
  pending_review: { bg: 'rgba(245,158,11,0.12)',  text: '#f59e0b',  label: 'Pending review' },
  published:      { bg: 'rgba(16,185,129,0.12)',  text: '#10b981',  label: 'Published' },
  scheduled:      { bg: 'rgba(14,165,233,0.12)',  text: '#0ea5e9',  label: 'Scheduled' },
  paused:         { bg: 'rgba(245,158,11,0.12)',  text: '#f59e0b',  label: 'Paused' },
  closed:         { bg: 'rgba(239,68,68,0.12)',   text: '#ef4444',  label: 'Closed' },
  delisted:       { bg: 'rgba(239,68,68,0.12)',   text: '#ef4444',  label: 'Delisted' },
};
function StatusPill({ status }: { status: string | null | undefined }) {
  const c = PS_MAP[status ?? 'draft'] ?? PS_MAP.draft;
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold"
      style={{ background: c.bg, color: c.text }}>{c.label}</span>
  );
}

/* ─── Mini horizontal stepper for wizard steps ─── */
const P = '#8B80F9';
function WizardStepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="flex items-center w-full py-2">
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <div key={label} className="flex items-center" style={{ flex: i < steps.length - 1 ? '1' : '0 0 auto' }}>
            <div className="flex flex-col items-center gap-1">
              <span className="flex items-center justify-center rounded-full text-xs font-bold transition-all"
                style={{
                  width: 28, height: 28,
                  ...(done
                    ? { background: P, color: '#fff' }
                    : active
                      ? { background: `color-mix(in srgb,${P} 15%,transparent)`, color: P, boxShadow: `0 0 0 2px ${P}` }
                      : { background: 'var(--vscode-bg-tertiary)', color: 'var(--vscode-text-tertiary)', border: '1.5px solid var(--vscode-border-primary)' }),
                }}>
                {done ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> : n}
              </span>
              <span className="text-[10px] font-semibold whitespace-nowrap"
                style={{ color: active ? P : done ? P : 'var(--vscode-text-tertiary)' }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className="flex-1 mx-1 mt-[-14px]">
                <div style={{ height: 2, borderRadius: 2, background: done ? P : 'var(--vscode-border-primary)', opacity: done ? 1 : 0.4 }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Main modal ─── */
export function JobLifecycleModal({
  open, onClose, job, orgId, orgSlug, orgName, onJobUpdated, initialAxis = 'publish',
}: {
  open: boolean; onClose: () => void; job: Job; orgId: string;
  orgSlug: string | null; orgName: string | null;
  onJobUpdated: (j: Job) => void; initialAxis?: 'ops' | 'publish';
}) {
  /* publish status */
  const ps = job.publish_status ?? 'draft';

  /* derive wizard steps based on publish status */
  const isManageMode = ['published', 'paused', 'closed', 'delisted'].includes(ps);
  const STEPS = isManageMode
    ? ['Channels', 'Manage listing']
    : ['Compose email', 'Target channels', 'Confirm & publish'];

  const [wizStep, setWizStep] = useState(1);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [channelDraft, setChannelDraft] = useState<JobPublishChannelId[]>([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [aiOpen, setAiOpen] = useState(false);

  /* inline SPOC email add */
  const [addingSpocEmail, setAddingSpocEmail] = useState(false);
  const [spocEmailInput, setSpocEmailInput] = useState('');
  const [spocEmailErr, setSpocEmailErr] = useState('');
  const [spocEmailBusy, setSpocEmailBusy] = useState(false);
  const [actionConfirm, setActionConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  const requestConfirm = (title: string, message: string, run: () => void) => {
    setActionConfirm({ title, message, onConfirm: run });
  };

  const saveSpocEmail = async () => {
    const val = spocEmailInput.trim();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setSpocEmailErr('Please enter a valid email address.');
      return;
    }
    if (!job.client_id) { setSpocEmailErr('No client linked to this job.'); return; }
    setSpocEmailBusy(true); setSpocEmailErr('');
    try {
      const { error } = await supabase
        .from('ggj_b2b_clients')
        .update({ contact_email: val })
        .eq('id', job.client_id);
      if (error) throw error;
      await refresh();
      setAddingSpocEmail(false);
      setSpocEmailInput('');
    } catch (e: unknown) { setSpocEmailErr(e instanceof Error ? e.message : 'Could not save email'); }
    finally { setSpocEmailBusy(false); }
  };

  useEffect(() => {
    if (!open) return;
    setErr(''); setWizStep(1); setCopied(false);
    setAddingSpocEmail(false); setSpocEmailInput(''); setSpocEmailErr('');
    const isPending = ps === 'pending_review';
    setEmailSubject(APPROVAL_SUBJECT(job.title));
    setEmailBody(isPending ? FOLLOWUP_BODY(job.title) : APPROVAL_BODY(job.title, orgName));
    setScheduleAt(
      ps === 'scheduled' && job.scheduled_publish_at
        ? job.scheduled_publish_at.slice(0, 16) : ''
    );
  }, [open, job.id, job.title, orgName, ps, job.scheduled_publish_at]);

  useEffect(() => {
    if (!open) return;
    setChannelDraft(normalizeJobPublishChannels(job.publish_channels));
  }, [open, job.id, job.publish_channels]);

  const refresh = async () => {
    const j = await jobService.getById(job.id);
    if (j) onJobUpdated(j);
  };

  const applyPublishStatus = async (actionId: string, next: JobPublishStatus, extra: Record<string, unknown> = {}) => {
    const cur = job.publish_status ?? 'draft';
    if (!isJobPublishTransitionAllowed(cur, next)) { setErr(`Cannot move from ${cur} to ${next}.`); return; }
    setBusy(actionId); setErr('');
    try {
      if (cur === 'scheduled' && next !== 'scheduled')
        await scheduledJobService.cancelPendingPublishJobsForB2BJob(orgId, job.id);
      const patch: Record<string, unknown> = { publish_status: next, ...extra };
      if (next === 'published') {
        patch.published_at = new Date().toISOString();
        if (!normalizeJobPublishChannels(job.publish_channels).length) patch.publish_channels = ['careers'];
      } else if (next === 'draft' && extra.published_at === null) { patch.published_at = null; }
      if (next !== 'scheduled') {
        if (!('scheduled_publish_at' in patch)) patch.scheduled_publish_at = null;
        if (!('scheduled_publish_job_id' in patch)) patch.scheduled_publish_job_id = null;
      }
      const { error } = await supabase.from('ggj_b2b_jobs').update(patch).eq('id', job.id);
      if (error) throw error;
      await refresh();
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Update failed'); }
    finally { setBusy(null); }
  };

  const saveSchedule = async () => {
    if (!scheduleAt) return;
    setBusy('sched'); setErr('');
    try {
      await scheduledJobService.scheduleJobPublish(orgId, job.id, new Date(scheduleAt).toISOString());
      await refresh(); setScheduleAt('');
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Schedule failed'); }
    finally { setBusy(null); }
  };

  const saveChannels = async () => {
    setBusy('channels'); setErr('');
    try {
      await jobService.update(job.id, { publish_channels: normalizeJobPublishChannels(channelDraft) });
      await refresh();
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Could not save channels'); }
    finally { setBusy(null); }
  };

  const handleSendEmail = async () => {
    const mailto = buildClientListingApprovalMailto({ clientEmail: job.client?.contact_email, jobTitle: job.title, orgName });
    if (mailto) window.open(mailto, '_blank');
    await applyPublishStatus('to_review', 'pending_review');
    setWizStep(2);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${emailSubject}\n\n${emailBody}`);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { setErr('Could not copy'); }
  };

  const clientEmail = job.client?.contact_email;
  const careersUrl = typeof window !== 'undefined' && orgSlug
    ? `${window.location.origin}/careers/${encodeURIComponent(orgSlug)}` : null;
  const totalSteps = STEPS.length;

  if (!open) return null;

  /* ─── step renderers ─── */
  const renderStep = () => {
    /* MANAGE MODE */
    if (isManageMode) {
      if (wizStep === 1) return renderChannels();
      return renderManage();
    }
    /* WIZARD MODE */
    if (wizStep === 1) return renderEmailStep();
    if (wizStep === 2) return renderChannels();
    return renderConfirm();
  };

  /* Step A — email draft */
  const renderEmailStep = () => (
    <div className="space-y-4">
      <div className="rounded-xl border vscode-border p-4 space-y-3"
        style={{ background: 'var(--vscode-bg-secondary)' }}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold vscode-text-primary flex items-center gap-2">
            <Mail className="w-4 h-4" style={{ color: P }} />
            {ps === 'pending_review' ? 'Follow-up email to client' : 'Request client approval'}
          </p>
          <AiAssistIconButton onClick={() => setAiOpen(true)} title="AI — write email" />
        </div>
        <p className="text-xs vscode-text-secondary leading-relaxed">
          {ps === 'pending_review'
            ? 'Send a follow-up to the client to confirm listing approval.'
            : 'Email the hiring client for written approval before listing on the careers site. After sending, status moves to Pending review.'}
        </p>

        <div className="space-y-2">
          <div>
            <label className="label text-[11px] mb-1">To</label>
            {clientEmail
              ? <input readOnly className="input w-full text-sm py-2" value={clientEmail} />
              : addingSpocEmail
                ? (
                  <div className="space-y-2">
                    <div className="flex gap-2 items-center">
                      <input
                        type="email"
                        autoFocus
                        className="input flex-1 text-sm py-2"
                        placeholder="spoc@client.com"
                        value={spocEmailInput}
                        onChange={(e) => { setSpocEmailInput(e.target.value); setSpocEmailErr(''); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void saveSpocEmail(); } if (e.key === 'Escape') { setAddingSpocEmail(false); } }}
                        disabled={spocEmailBusy}
                      />
                      <button
                        type="button"
                        disabled={spocEmailBusy || !spocEmailInput.trim()}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
                        style={{ background: P }}
                        onClick={() => void saveSpocEmail()}>
                        {spocEmailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Save
                      </button>
                      <button type="button" className="text-xs vscode-text-tertiary hover:vscode-text-primary px-2 py-2" onClick={() => { setAddingSpocEmail(false); setSpocEmailInput(''); setSpocEmailErr(''); }}>
                        Cancel
                      </button>
                    </div>
                    {spocEmailErr && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {spocEmailErr}
                      </p>
                    )}
                  </div>
                )
                : (
                  <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 border border-amber-500/30"
                    style={{ background: 'color-mix(in srgb,#f59e0b 8%,var(--vscode-bg-secondary))' }}>
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      No contact email on client record
                    </span>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md text-white shrink-0 transition-all"
                      style={{ background: P }}
                      onClick={() => { setAddingSpocEmail(true); setSpocEmailInput(''); setSpocEmailErr(''); }}>
                      <Plus className="w-3 h-3" /> Add email
                    </button>
                  </div>
                )
            }
          </div>
          <div>
            <label className="label text-[11px] mb-1">Subject</label>
            <input className="input w-full text-sm py-2" value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label text-[11px] mb-0">Body</label>
              <button type="button" onClick={() => setAiOpen(true)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md"
                style={{ color: P, background: 'rgba(139,128,249,0.1)' }}>
                <Sparkles className="w-3 h-3" /> AI write
              </button>
            </div>
            <textarea className="input w-full resize-none text-sm py-2" rows={7}
              value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          {clientEmail
            ? <button type="button" disabled={!!busy} className="btn-primary inline-flex items-center gap-2"
                onClick={() => void handleSendEmail()}>
                {busy === 'to_review' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send &amp; mark pending review
              </button>
            : <button type="button" className="btn-secondary inline-flex items-center gap-2"
                onClick={() => void copyEmail()}>
                <Copy className="w-4 h-4" /> {copied ? 'Copied ✓' : 'Copy email text'}
              </button>
          }
          <button type="button" disabled={!!busy} className="btn-secondary inline-flex items-center gap-2"
            onClick={() => void applyPublishStatus('to_review', 'pending_review').then(() => setWizStep(2))}>
            Mark pending (sent separately)
          </button>
        </div>
      </div>
    </div>
  );

  /* Step B — channels */
  const renderChannels = () => (
    <div className="rounded-xl border vscode-border p-4 space-y-4"
      style={{ background: 'var(--vscode-bg-secondary)' }}>
      <p className="text-sm font-bold vscode-text-primary">Target channels</p>
      <p className="text-xs vscode-text-secondary leading-relaxed">
        Only <strong className="vscode-text-primary">Organisation careers site</strong> affects live behaviour today;
        other options record intent for future syndication.
      </p>
      <ul className="space-y-3">
        {JOB_PUBLISH_CHANNEL_DEFS.map((def) => (
          <li key={def.id}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" className="mt-0.5 rounded" disabled={!!busy}
                checked={channelDraft.includes(def.id)}
                onChange={(e) => setChannelDraft((prev) =>
                  e.target.checked
                    ? prev.includes(def.id) ? prev : [...prev, def.id]
                    : prev.filter((x) => x !== def.id)
                )} />
              <span>
                <span className="text-sm vscode-text-primary font-medium">{def.label}</span>
                {!def.live && <span className="text-[11px] text-amber-500 ml-1.5 font-semibold">(planned)</span>}
                <span className="block text-xs vscode-text-secondary mt-0.5 leading-relaxed">{def.helper}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <button type="button" className="btn-primary inline-flex items-center gap-2"
        disabled={!!busy || publishChannelsDraftMatchesJob(channelDraft, job.publish_channels)}
        onClick={() => void saveChannels()}>
        {busy === 'channels' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Save channel targets
      </button>
    </div>
  );

  /* Step C — confirm & publish */
  const renderConfirm = () => (
    <div className="space-y-4">
      {/* review summary */}
      <div className="rounded-xl border vscode-border p-4 space-y-3"
        style={{ background: 'var(--vscode-bg-secondary)' }}>
        <p className="text-sm font-bold vscode-text-primary flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Review &amp; confirm
        </p>
        <div className="space-y-2">
          {[
            { key: 'Job title', value: job.title },
            { key: 'Client', value: job.client?.name ?? '—' },
            { key: 'Channels', value: channelDraft.length ? channelDraft.join(', ') : 'None selected' },
            { key: 'Current status', value: ps.replace('_', ' ') },
          ].map(({ key, value }) => (
            <div key={key} className="flex items-baseline gap-3">
              <span className="text-xs vscode-text-tertiary w-28 shrink-0">{key}</span>
              <span className="text-sm vscode-text-primary font-medium">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* publish actions */}
      {ps === 'draft' && (
        <div className="rounded-xl border vscode-border p-4 space-y-3"
          style={{ background: 'var(--vscode-bg-secondary)' }}>
          <p className="text-sm font-semibold vscode-text-primary">Publish options</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={!!busy} className="btn-primary inline-flex items-center gap-2"
              onClick={() => {
                if (job.status !== 'open') {
                  requestConfirm('Publish anyway?', 'Ops not Open. Publish anyway?', () =>
                    void applyPublishStatus('pub_now', 'published'),
                  );
                  return;
                }
                void applyPublishStatus('pub_now', 'published');
              }}>
              {busy === 'pub_now' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Publish now
            </button>
            <button type="button" disabled={!!busy} className="btn-secondary inline-flex items-center gap-2"
              onClick={() => void applyPublishStatus('to_sched', 'scheduled')}>
              <Clock className="w-4 h-4" /> Schedule…
            </button>
          </div>
          {ps === 'scheduled' && (
            <div className="space-y-2 pt-2">
              <label className="label text-[11px]">Go-live time</label>
              <input type="datetime-local" className="input text-sm py-2 max-w-xs"
                value={scheduleAt} min={new Date().toISOString().slice(0, 16)}
                onChange={(e) => setScheduleAt(e.target.value)} />
              <button type="button" className="btn-primary inline-flex items-center gap-2"
                disabled={!scheduleAt || !!busy} onClick={() => void saveSchedule()}>
                {busy === 'sched' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save schedule
              </button>
            </div>
          )}
        </div>
      )}

      {ps === 'pending_review' && (
        <div className="rounded-xl border vscode-border p-4 space-y-3"
          style={{ background: 'var(--vscode-bg-secondary)' }}>
          <p className="text-sm font-semibold vscode-text-primary">Client has confirmed?</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={!!busy} className="btn-primary inline-flex items-center gap-2"
              onClick={() => {
                if (job.status !== 'open') {
                  requestConfirm('Approve anyway?', 'Ops not Open. Approve anyway?', () =>
                    void applyPublishStatus('approve_pub', 'published'),
                  );
                  return;
                }
                void applyPublishStatus('approve_pub', 'published');
              }}>
              {busy === 'approve_pub' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Approve &amp; publish
            </button>
            <button type="button" disabled={!!busy} className="btn-secondary inline-flex items-center gap-2"
              onClick={() => void applyPublishStatus('to_sched_state', 'scheduled')}>
              <Clock className="w-4 h-4" /> Schedule instead
            </button>
            <button type="button" disabled={!!busy} className="btn-secondary"
              onClick={() => void applyPublishStatus('back_draft', 'draft')}>
              Back to draft
            </button>
          </div>
        </div>
      )}
    </div>
  );

  /* manage mode — published / paused / closed */
  const renderManage = () => (
    <div className="rounded-xl border vscode-border p-4 space-y-4"
      style={{ background: 'var(--vscode-bg-secondary)' }}>
      <div className="flex items-center gap-3">
        <StatusPill status={ps} />
        {careersUrl && ps === 'published' && job.status === 'open' && (
          <a href={careersUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:underline">
            <ExternalLink className="w-3.5 h-3.5" /> Open careers page
          </a>
        )}
      </div>

      {ps === 'scheduled' && (
        <div className="space-y-2">
          <p className="text-xs vscode-text-secondary">
            Queued: {job.scheduled_publish_at ? formatDateTimeGmt(job.scheduled_publish_at) : '—'}
          </p>
          <input type="datetime-local" className="input text-sm py-2 max-w-xs"
            value={scheduleAt} min={new Date().toISOString().slice(0, 16)}
            onChange={(e) => setScheduleAt(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-primary inline-flex items-center gap-2"
              disabled={!scheduleAt || !!busy} onClick={() => void saveSchedule()}>
              {busy === 'sched' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
              Save schedule
            </button>
            <button type="button" className="btn-secondary" disabled={!!busy}
              onClick={() => void applyPublishStatus('cancel_sched', 'draft', { scheduled_publish_at: null, scheduled_publish_job_id: null })}>
              Cancel → draft
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {ps === 'published' && (
          <>
            <button type="button" className="btn-secondary inline-flex items-center gap-2"
              disabled={!!busy} onClick={() => void applyPublishStatus('pause', 'paused')}>
              {busy === 'pause' ? <Loader2 className="w-4 h-4 animate-spin" /> : <PauseCircle className="w-4 h-4" />}
              Pause listing
            </button>
            <button type="button" disabled={!!busy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border border-red-500/40 text-red-500 hover:bg-red-500/10 transition-colors"
              onClick={() =>
                requestConfirm('Close listing?', 'Close listing?', () =>
                  void applyPublishStatus('pub_close', 'closed'),
                )
              }>
              <Ban className="w-4 h-4" /> Close / delist
            </button>
          </>
        )}
        {ps === 'paused' && (
          <>
            <button type="button" className="btn-primary inline-flex items-center gap-2"
              disabled={!!busy} onClick={() => void applyPublishStatus('resume_pub', 'published')}>
              {busy === 'resume_pub' ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              Resume listing
            </button>
            <button type="button" disabled={!!busy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border border-red-500/40 text-red-500 hover:bg-red-500/10 transition-colors"
              onClick={() =>
                requestConfirm('Close listing?', 'Close listing?', () =>
                  void applyPublishStatus('pause_close', 'closed'),
                )
              }>
              Close listing
            </button>
          </>
        )}
        {(ps === 'closed' || ps === 'delisted') && (
          <button type="button" className="btn-secondary inline-flex items-center gap-2"
            disabled={!!busy} onClick={() => void applyPublishStatus('reopen_draft', 'draft', { published_at: null })}>
            {busy === 'reopen_draft' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Reset to draft (re-list later)
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ConfirmDialog
        open={actionConfirm != null}
        onClose={() => setActionConfirm(null)}
        onConfirm={() => {
          const a = actionConfirm;
          setActionConfirm(null);
          a?.onConfirm();
        }}
        title={actionConfirm?.title ?? ''}
        message={actionConfirm?.message ?? ''}
        confirmLabel="Continue"
        variant="primary"
      />
      <Modal open={open} onClose={onClose}
        title="Publish / careers"
        subtitle={`${job.title} · ${job.client?.name ?? ''}`}
        icon={<Briefcase className="w-4 h-4" />}
        size="xl">

        <div className="flex flex-col" style={{ maxHeight: 'calc(85vh - 9rem)' }}>

          {/* ── zone 1: status pill + single wizard stepper ── */}
          <div className="shrink-0 pb-4 border-b vscode-border mb-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs vscode-text-tertiary leading-relaxed max-w-[70%] line-clamp-2">
                {JOB_PUBLISH_LIFECYCLE_HELPER}
              </p>
              <StatusPill status={ps} />
            </div>
            <WizardStepper steps={STEPS} current={wizStep} />
          </div>

          {err && <p className="shrink-0 text-xs text-red-500 font-medium mb-3">{err}</p>}

          {/* ── zone 2: scrollable step content ── */}
          <div className="flex-1 overflow-y-auto min-h-0 pr-1">
            {renderStep()}
          </div>

          {/* ── zone 3: prev / next navigation ── */}
          <div className="shrink-0 flex items-center justify-between gap-3 pt-4 border-t vscode-border mt-4">
            <button type="button" disabled={wizStep === 1}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-30"
              style={{ color: P, border: `1.5px solid ${P}`, background: 'transparent' }}
              onClick={() => { setErr(''); setWizStep((s) => s - 1); }}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            {wizStep < totalSteps ? (
              <button type="button"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all"
                style={{ background: P, boxShadow: '0 2px 8px rgba(139,128,249,0.4)' }}
                onClick={() => { setErr(''); setWizStep((s) => s + 1); }}>
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" className="btn-secondary text-sm" onClick={onClose}>
                Done
              </button>
            )}
          </div>

        </div>
      </Modal>

      <ContextAiAssistModal
        open={aiOpen} onClose={() => setAiOpen(false)}
        orgId={orgId} feature="ai_assist_publish_email" preset="email"
        title="AI — approval / follow-up email"
        contextHint={`Job: ${job.title}\nClient: ${job.client?.name ?? ''}\nStatus: ${ps}`}
        original={emailBody}
        onApply={(text, mode) =>
          setEmailBody(mode === 'replace' ? text : emailBody.trim() ? `${emailBody.trim()}\n\n${text}` : text)
        }
      />
    </>
  );
}
